import { useCallback, useRef } from 'react';

import type { ValidationState } from './types.js';
import { useCallbackRef } from '../../core/hooks/useCallbackRef.js';

type ValidationStateSignature = {
  errorKeys: string[];
  trigger: ValidationState['trigger'];
};

/**
 * Emits validation changes only when the public validation summary changes.
 *
 * @internal
 */
export function useValidationStateEmitter(
  onValidityChange: ((state: ValidationState) => void) | undefined,
): (state: ValidationState) => void {
  const lastSignatureRef = useRef<ValidationStateSignature>(undefined);
  const onValidityChangeCallback = useCallbackRef(onValidityChange);

  return useCallback(
    (state: ValidationState) => {
      const lastSignature = lastSignatureRef.current;

      const nextSignature = {
        errorKeys: state.errors
          .map((e) => e.validityStateKey.toString())
          .sort((a, b) => a.localeCompare(b)),
        trigger: state.trigger,
      };

      if (lastSignature === undefined && nextSignature.trigger === 'change') {
        return;
      }

      const sameErrors =
        lastSignature !== undefined &&
        lastSignature.errorKeys.length === nextSignature.errorKeys.length &&
        lastSignature.errorKeys.every(
          (key, i) => key === nextSignature.errorKeys[i],
        );

      if (sameErrors && nextSignature.trigger !== 'manual') {
        return;
      }

      lastSignatureRef.current = nextSignature;
      onValidityChangeCallback(state);
    },
    [onValidityChangeCallback],
  );
}
